import { Modal } from '@klear/ui/components/modal'
import { Alert, AlertDescription, AlertTitle } from '@klear/ui/components/alert'
import LoadingSpinner from '@klear/ui/components/spinner'
import { trpc } from '@klear/ui/lib/trpc'
import toString from 'lodash/toString'

interface VerificationErrorDialogProps {
  accountId: number
  bankName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function DiagnosticRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  const displayValue = value == null || value === '' ? '—' : toString(value)
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-3 text-sm">
      <span className="font-medium text-muted-foreground">{label}</span>
      <span className="break-all font-mono text-xs">{displayValue}</span>
    </div>
  )
}

export default function VerificationErrorDialog({
  accountId,
  bankName,
  open,
  onOpenChange
}: VerificationErrorDialogProps) {
  const { data, isLoading, error } = trpc.traderExternalBankAccount.getVerificationDiagnostics.useQuery(
    { id: accountId },
    { enabled: open && accountId > 0 }
  )

  return (
    <Modal
      title="Verification diagnostics"
      description={`Stripe SetupIntent details for ${bankName}. Klear internal only.`}
      open={open}
      onOpenChange={onOpenChange}
      primaryText="Close"
      handlePrimaryAction={() => onOpenChange(false)}
      size="md"
    >
      <div className="space-y-4">
        {isLoading && (
          <div className="py-8">
            <LoadingSpinner className="min-h-0! h-auto" />
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTitle>Failed to load diagnostics</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        {data && (
          <>
            {data.lastSetupError ? (
              <Alert variant={data.requiresSupport ? 'destructive' : 'default'}>
                <AlertTitle>{data.lastSetupError.code ?? 'SetupIntent error'}</AlertTitle>
                <AlertDescription>{data.lastSetupError.message ?? 'No error message from Stripe.'}</AlertDescription>
              </Alert>
            ) : (
              <Alert>
                <AlertTitle>No Stripe error recorded</AlertTitle>
                <AlertDescription>
                  Stripe did not return a last_setup_error for this SetupIntent. Check status and account fields below.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-3 rounded-md border p-4">
              <DiagnosticRow label="Account status" value={data.accountVerificationStatus} />
              <DiagnosticRow label="SetupIntent ID" value={data.setupIntentId} />
              <DiagnosticRow label="SetupIntent status" value={data.status} />
              <DiagnosticRow label="Verification method" value={data.verificationMethod} />
              <DiagnosticRow label="Next action" value={data.nextActionType} />
              <DiagnosticRow label="Attempts used" value={data.verificationAttempts} />
              <DiagnosticRow label="Requires support" value={data.requiresSupport ? 'yes' : 'no'} />
              <DiagnosticRow label="Payment method" value={data.stripePaymentMethodId} />
              <DiagnosticRow label="Cancellation reason" value={data.cancellationReason} />
            </div>

            {data.lastSetupError && (
              <div className="space-y-3 rounded-md border p-4">
                <p className="text-sm font-medium">Last setup error</p>
                <DiagnosticRow label="Type" value={data.lastSetupError.type} />
                <DiagnosticRow label="Code" value={data.lastSetupError.code} />
                <DiagnosticRow label="Decline code" value={data.lastSetupError.declineCode} />
                <DiagnosticRow label="Doc URL" value={data.lastSetupError.docUrl} />
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
